import { BROADCASTCHANNEL_TYPES, SetupBroadcastChannel } from '@/Data/Functions/BroadcastChannels';
import '@/Data/Functions/arrayExtensions';
import auth from '@/Data/Functions/auth';
import interceptors from '@/Data/Functions/axiosInterceptors';
import { convertToStoreTimeZone } from '@/Data/Functions/dateLocalisation';
import hotkey from '@/Data/Functions/hotkey';
import message from '@/Data/Functions/message';
import updateMetaTitle from '@/Data/Functions/metaTitle';
import '@/Data/Functions/stringExtensions';
import APIURLS from '@/Data/Objects/apiUrls';
import dateTimeFormats from '@/Data/Objects/i18nDateFormats';
import numberFormats from '@/Data/Objects/i18nNumberFormats';
import { messages } from '@/i18n';
import loadingButton from '@/mixins/LoadingButton';
import route from '@/mixins/Route';
import SeqLogger from '@/mixins/SeqLogger';
import serviceWorkersAuthChecker from '@/mixins/ServiceWorkers-AuthChecker';
import serviceWorkersVersionChecker from '@/mixins/ServiceWorkers-VersionChecker';
import vueErrorHandler from '@/plugins/ErrorHandler';
import axios from 'axios';
import { DateTime, Settings } from 'luxon';
import Vue from 'vue';
import VueI18n from 'vue-i18n';
import * as VueGoogleMaps from 'vue2-google-maps';
import { mapActions, mapGetters } from 'vuex';
import App from './App.vue';
import router from './router';
import store from './store';

window.appVersionWorkerSet = false;
Vue.config.productionTip = false;

Vue.use(vueErrorHandler);
Vue.use(VueI18n);

Vue.use(VueGoogleMaps, {
    load: {
        key: 'AIzaSyAKd5egEIdjFJtQLj4RQ3aUgFGvNGrG4t4',
        libraries: 'places'
        // v: '3.26',
    },
    installComponents: true
});

const i18n = new VueI18n({
    locale: 'en-GB',
    fallbackLocale: 'en-GB',
    messages,
    numberFormats,
    dateTimeFormats
});

// Add a request interceptor
interceptors.request(axios);

/**
 * Allows axios to be used everywhere by typing this.$api.verb(...options), where verb is either; get, put, post or delete
 */
Vue.prototype.$api = axios;
Vue.prototype.$meta = updateMetaTitle;

Vue.prototype.$broadcast = {
    user: SetupBroadcastChannel(BROADCASTCHANNEL_TYPES.CLOUD_MT_USER_CHANGED)
};

Vue.prototype.$ga = (eventCategory, eventAction, eventLabel) => {
    // maybe add some defaults etc.
    ga('send', {
        hitType: 'event',
        eventCategory: eventCategory,
        eventAction: eventAction,
        eventLabel: eventLabel
    });
};

/**
 * Creates a new Vue instance. This is the Vue root.
 */
new Vue({
    el: '#app',
    mixins: [
        loadingButton,
        route,
        serviceWorkersVersionChecker,
        serviceWorkersAuthChecker,
        SeqLogger
    ],
    /**
     * Root-level data which needs to be available everywhere.
     */
    data() {
        return {
            appTitle: '',
            appVersion: process.env.VUE_APP_VERSION,
            user: auth.userinfo().userinfo,
            super: auth.superStatus(),
            apiUrls: APIURLS,
            ignore400Errors: {
                headers: {
                    Ignore400Errors: 'true'
                }
            },
            ignore404Errors: {
                headers: {
                    Ignore404Errors: 'true'
                }
            },
            Ignore401Errors: {
                headers: {
                    Ignore401Errors: 'true'
                }
            },
            config: Object,
            localisationInfo: {},
            userIsLoggedIn: auth.status(),
            hotkeyPopupVisible: false,
            productSearchPopupVisible: false,
            saveFunction() { },
            fatalError: {
                appHasFatalError: false,
                message: '',
                endpoint: ''
            },
            showChangeSaveIndicator: false,
            customChangeSaveIndicatorText: '',
            logoutText: 'Logout of Cloud MT'
        };
    },
    computed: {
        ...mapGetters({
            locale: 'getLocale',
            timeZone: 'getTimeZone'
        }),
        changeSaveIndicatorComponent() {
            return this.showChangeSaveIndicator ? 'ChangeSaveIndicator' : null;
        },
        appPageTitle: {
            get() {
                return this.appTitle;
            },
            set(value) {
                this.appTitle = value;
            }
        },
        showAppReloadOverlay() {
            return ((this.idleWorkerVersionChecker.workerTimeStamp != this.idleWorkerVersionChecker.buildTimestamp) && (this.idleWorkerVersionChecker.buildTimestamp != 0 && this.idleWorkerVersionChecker.workerTimeStamp != 0));
        },
        storeCurrency() {
            let currencyToReturn = 'GBP';

            if (this.config.isoCurrency != undefined) {
                currencyToReturn = this.config.isoCurrency;
            }

            return currencyToReturn;
        },
        storeCurrencySymbol() {
            const formattedValue = this.$n(1_234_567.89, 'currency');

            return formattedValue.match(/[^=0-9\.,\-\s]*/g).join('');
        },
        siteURL() {
            let stringToReturn = '';

            if (this.config.siteUrl != undefined) {
                stringToReturn = this.config.siteUrl;
            }

            return stringToReturn;
        },
        appBuildDate() {
            return convertToStoreTimeZone(DateTime.fromMillis(parseInt(this.appVersion)))
                .toFormat(this.dateTimeFormat);
        },
        monthAndYearFormat() {
            return this.locale === 'en-US' ? 'M/yyyy' : 'MM/yyyy';
        },
        dateFormat() {
            return this.locale === 'en-US' ? 'M/d/yyyy' : 'dd/MM/yyyy';
        },
        timeFormat() {
            return this.locale === 'en-US' ? 'hh:mm a' : 'HH:mm';
        },
        dateTimeFormat() {
            return this.locale === 'en-US' ? 'M/d/yyyy hh:mm a' : 'dd/MM/yyyy HH:mm';
        },
        dateTimeInputFormat() {
            return this.locale === 'en-US' ? 'yyyy-dd-MM\'T\'HH:mm' : 'yyyy-MM-dd\'T\'HH:mm';
        },
        longDateTimeFormat() {
            return this.locale === 'en-US' ? 'LLLL d, yyyy h:MM:ss a' : 'd LLLL yyyy, HH:MM:ss';
        }
    },
    /**
     * Add the router component to the app.
     */
    router,
    store,
    i18n,
    watch: {
        userIsLoggedIn() {
            this.configLoggedInStatusManagement();
        },
        '$route.fullPath'() {
            this.appPageTitle = this.$route.meta.title;
            this.productSearchPopupVisible = false;
        },
        showChangeSaveIndicator(newValue) {
            if (!newValue) {
                this.customChangeSaveIndicatorText = '';
            }
        },
        locale(newValue) {
            if (newValue) {
                this.setLocaleValues(newValue);
            }
        },
        timeZone(newValue) {
            if (newValue) {
                this.setTimeZoneValues(newValue);
            }
        },
        config: {
            handler(newValue) {
                if (newValue) {
                    this.setCurrentLocale(newValue.twoLetterISOName);
                }
            },
            deep: true
        },
        localisationInfo: {
            handler(newValue) {
                if (newValue) {
                    this.setCurrentTimeZone(newValue.groupTimeZone);
                }
            },
            deep: true
        }
    },
    created() {
        this.configLoggedInStatusManagement();
        this.setAxiosInterceptors();
        this.setAppVersion()
            .then(() => {
                this.initAppVersionWorker();
            });

        this.$router.onError(error => {
            if (/Loading chunk ROUTE___[a-zA-Z0-9]+ failed/gmi.test(error.message)) {
                this.showReloadOverlay();
            }
        });
    },
    mounted() {
        this.setLocaleValues(this.locale);

        this.addHotkeyEventListener();
        this.addTabFocusEventListener();
        this.setUserChangeListener();
    },
    beforeDestroy() {
        this.removeHotkeyEventListener();
        this.removeTabFocusEventListener();
    },
    methods: {
        ...mapActions([
            'setCurrentLocale',
            'setCurrentTimeZone'
        ]),
        setLocaleValues(value) {
            this.$i18n.locale = value;
            Settings.defaultLocale = value;
        },
        setTimeZoneValues(value) {
            Settings.defaultZone = value;
        },
        showMessage: message.showMessage,
        logout(event) {
            event.preventDefault();

            auth.remove();
            this.$root.userIsLoggedIn = auth.status();

            this.setCurrentLocale('en-GB');
            this.setCurrentTimeZone('Europe/London');

            this.$router.push({
                path: '/login',
                query: { redirect: this.$router.currentRoute.fullPath }
            });
        },
        getAdminConfig() {
            return this.$api.get(this.$root.apiUrls.admin.root)
                .then((response) => {
                    return response.data;
                })
                .catch(() => { });
        },
        getLocalisationInfo() {
            return this.$api.get(this.$root.apiUrls.localisation.root)
                .then((response) => response.data)
                .catch(() => { /* Handled by the global interceptor */ });
        },
        updateAdminConfig(adminDataToUpdate, e = null, suppressMessage = false) {
            if (e != null) {
                this.loadingButton(e, true);
            }

            this.$root.showChangeSaveIndicator = true;

            return this.performAdminConfigUpdate(adminDataToUpdate)
                .then((data) => {
                    if (!suppressMessage) {
                        this.$root.showMessage('Settings saved successfully.', 'success');
                    }

                    return data;
                })
                .catch(() => { /* Handled by the global interceptor */ })
                .finally(() => {
                    if (e != null) {
                        this.loadingButton(e, false);
                    }

                    this.$root.showChangeSaveIndicator = false;
                });
        },
        performAdminConfigUpdate(adminDataToUpdate) {
            return this.$api.put(this.$root.apiUrls.admin.root, adminDataToUpdate)
                .then((response) => {
                    this.$root.updatePageCacheRefreshToken();

                    this.$root.config = response.data;

                    return response.data;
                });
        },
        updatePageCacheRefreshToken() {
            return this.$api.post(this.$root.apiUrls.admin.updatePageCacheRefreshToken);
        },
        configLoggedInStatusManagement() {
            let result = Promise.resolve();

            /* get site settings when user is logged in */
            if (this.userIsLoggedIn) {
                const requests = [
                    this.getAdminConfig(),
                    this.getLocalisationInfo()
                ];

                result = this.$api.all(requests)
                    .then(this.$api.spread((adminResponse, localisationResponse) => {
                        this.$root.config = adminResponse.data;
                        this.$root.localisationInfo = localisationResponse;
                    }));
            }
            /* remove site settings when user is logged out */
            else {
                this.$root.config = Object;
                this.$root.localisationInfo = {};
            }

            this.user = auth.userinfo().userinfo;

            return result;
        },
        addHotkeyEventListener() {
            window.addEventListener('keydown', this.onKeyPress);
            window.addEventListener('keyup', hotkey.handleKeyRelease);
        },
        removeHotkeyEventListener() {
            window.removeEventListener('keydown', this.onKeyPress);
            window.removeEventListener('keyup', hotkey.handleKeyRelease);
        },
        onKeyPress(event) {
            if (hotkey.handleKeyPress(event)) {
                const hotkeyEvent = hotkey.checkKeyPress();

                if (hotkeyEvent) {
                    if (hotkeyEvent.securityLevelRequired.some(s => s === this.user.securityLevelName)) {
                        if (hotkeyEvent.path !== '') {
                            if (this.$route.path !== hotkeyEvent.path) {
                                this.hotkeyPopupVisible = false;
                                this.productSearchPopupVisible = false;

                                this.$router.push({
                                    path: hotkeyEvent.path
                                });
                            }
                        }
                        else {
                            switch (hotkeyEvent.name) {
                                case 'Overlay':
                                    UIkit.offcanvas('#nav-menu').hide();
                                    this.productSearchPopupVisible = false;
                                    this.hotkeyPopupVisible = !this.hotkeyPopupVisible;
                                    break;
                                case 'Search':
                                    UIkit.offcanvas('#nav-menu').hide();
                                    this.hotkeyPopupVisible = false;
                                    this.productSearchPopupVisible = !this.productSearchPopupVisible;
                                    break;
                                case 'Navigation':
                                    this.hotkeyPopupVisible = false;
                                    this.productSearchPopupVisible = false;
                                    UIkit.offcanvas('#nav-menu').toggle();
                                    break;
                                case 'Save':
                                    this.saveFunction();
                                    break;
                                case 'Close':
                                    this.hotkeyPopupVisible = false;
                                    this.productSearchPopupVisible = false;
                                    UIkit.offcanvas('#nav-menu').hide();
                                    break;
                            }
                        }
                    }
                    else {
                        const securityLevels = hotkeyEvent.securityLevelRequired
                            .map(s => `'${s}'`)
                            .join(', ');

                        UIkit.modal.alert(`You do not have the required permissions to perform this action. Your current security level is '${this.user.securityLevelName}' but one of the following is required: ${securityLevels}.`);
                    }
                }
            }
        },
        addTabFocusEventListener() {
            window.addEventListener('focus', this.delayedMeta);
        },
        removeTabFocusEventListener() {
            window.removeEventListener('focus', this.delayedMeta);
        },
        delayedMeta() {
            // Something changes the tab title to its previous value; this forces it to be correct
            setTimeout(() => {
                this.$meta(this.appPageTitle);
                this.setRouteTitle(this.appPageTitle);
            }, 100);
        },
        showReloadOverlay() {
            this.showAppReloadOverlay = true;
            this.$ga('App Event', 'Message', 'Page requires reload');
        },
        setAxiosInterceptors() {
            interceptors.response(axios, this);
        },
        setUserChangeListener() {
            if (this.$broadcast?.user) {
                this.$broadcast.user.onmessage = this.userChangedEvent;
            }
        },
        userChangedEvent() {
            const newUser = auth.userinfo().userinfo;

            if (this.user.groupId !== newUser.groupId &&
                this.user.name !== newUser.name) {
                this.user = newUser;
                this.userIsLoggedIn = auth.status();

                this.configLoggedInStatusManagement()
                    .then(() => this.$router.go());
            }
        }
    },
    /**
     * Render (create) the app.
     */
    render: h => h(App)
}).$mount('#app');
